import React, { ReactNode, useCallback, useEffect, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate } from "react-router-dom";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { Grid, Box, Card, Stack, Typography, Button } from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import NiceModal from "@ebay/nice-modal-react";

import { PageContainer } from "../common";
import { AccountInput, Checkbox } from "../common/forms";

import { isIdValid, signUp } from "src/services/accoutApi";
import Modal from "../common/Modal";
import { formatToFull } from "src/utils/dateFormatter";
import logo from "src/assets/images/logo2.png";

import {
  ID_REQUIRED_MESSAGE,
  PASSWORD_REQUIRED_MESSAGE,
  PASSWORD_CONFIRM_WARNING_MESSAGE,
  PASSWORD_LENGTH_WARNING_MESSAGE,
  NAME_REQUIRED_MESSAGE,
  EMAIL_REQUIRED_MESSAGE,
  EMAIL_WARNING_MESSAGE,
  PHONE_REQUIRED_MESSAGE,
  PHONE_WARNING_MESSAGE
} from "src/common/constants";

interface FormInputs {
  mbr_id: string;
  mbr_nm: string;
  mbr_pw: string;
  mbr_pw_check: string;
  mbr_tel: string;
  mbr_email: string;
  mbr_empl_dt?: Date;
}

export const SignUpSchema = yup
  .object({
    mbr_id: yup.string().required(ID_REQUIRED_MESSAGE),
    mbr_nm: yup.string().required(NAME_REQUIRED_MESSAGE),
    mbr_pw: yup
      .string()
      .trim()
      .required(PASSWORD_REQUIRED_MESSAGE)
      .min(8, PASSWORD_LENGTH_WARNING_MESSAGE)
      .max(15, PASSWORD_LENGTH_WARNING_MESSAGE),
    mbr_pw_check: yup
      .string()
      .trim()
      .required(PASSWORD_REQUIRED_MESSAGE)
      .oneOf([yup.ref("mbr_pw"), ""], PASSWORD_CONFIRM_WARNING_MESSAGE),
    mbr_tel: yup
      .string()
      .required(PHONE_REQUIRED_MESSAGE)
      .matches(/^[0-9]{11}/i, PHONE_WARNING_MESSAGE),
    mbr_email: yup
      .string()
      .email(EMAIL_WARNING_MESSAGE)
      .required(EMAIL_REQUIRED_MESSAGE)
  })
  .required();

const AuthRegister = ({
  title,
  subtitle,
  subtext
}: {
  title?: ReactNode;
  subtitle: ReactNode;
  subtext?: ReactNode;
}) => {
  const navigate = useNavigate();

  const [duplicateChecked, setDuplicateChecked] = useState(false);
  // 이용약관이 2개뿐이라 네이밍과 구현 방식 이렇게 구현. 많아지면 개선하도록.
  const [terms1, setTerms1] = useState(false);
  const [terms2, setTerms2] = useState(false);
  const [terms1Modal, setTerms1Modal] = useState(false);
  const [terms2Modal, setTerms2Modal] = useState(false);

  const methods = useForm<FormInputs>({
    defaultValues: {
      mbr_id: "",
      mbr_nm: "",
      mbr_pw: "",
      mbr_pw_check: "",
      mbr_tel: "",
      mbr_email: ""
    },
    resolver: yupResolver(SignUpSchema)
  });

  const { watch } = methods;
  const id = watch("mbr_id");

  useEffect(() => {
    setDuplicateChecked(false);
  }, [id]);

  const onSubmit: SubmitHandler<FormInputs> = useCallback(
    async data => {
      if (!duplicateChecked) {
        NiceModal.show("alert", {
          title: "아이디 중복체크를 해주세요."
        });
      } else {
        const {
          mbr_id,
          mbr_pw,
          mbr_nm,
          mbr_tel,
          mbr_empl_dt: joinDate,
          mbr_email
        } = data;
        const formattedDate = formatToFull(joinDate);

        try {
          signUp({
            mbr_id,
            mbr_pw,
            mbr_nm,
            mbr_tel,
            mbr_empl_dt: formattedDate,
            mbr_email
          });

          NiceModal.show("alert", {
            title: "계정신청이 완료되었습니다.",
            message:
              "관리자 승인후 계정 사용이 가능합니다. 확인을 누르면 로그인페이지로 이동합니다."
          }).then(() => {
            navigate("/signin");
          });
        } catch (error) {
          NiceModal.show("alert", {
            title: "계정신청을 완료하지 못했습니다.",
            message:
              "잠시후 다시 시도해주세요. 문제가 계속될 경우 관리자에게 문의 바랍니다."
          });
        }
      }
    },
    [navigate, duplicateChecked]
  );

  const handleCheckId = useCallback(async (v: string) => {
    if (!v) {
      NiceModal.show("alert", {
        title: "아이디 입력 후 중복체크해주세요."
      });
    } else {
      try {
        const { data } = await isIdValid(v);

        NiceModal.show("alert", {
          title: data.message
        }).then(() => {
          setDuplicateChecked(data.data);
        });
      } catch {
        setDuplicateChecked(false);
      }
    }
  }, []);

  return (
    <>
      {title ? (
        <Typography fontWeight="700" variant="h3" mb={1}>
          {title}
        </Typography>
      ) : null}

      {subtext}

      <Box>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Stack mb={3}>
              <Stack direction="row" alignItems="flex-end" gap="4px">
                <AccountInput name="mbr_id" label="아이디" required />
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => handleCheckId(id)}
                  disableRipple
                  sx={{ whiteSpace: "nowrap", height: "44px", width: "80px" }}
                >
                  {duplicateChecked ? (
                    <CheckCircle color="success" />
                  ) : (
                    "중복체크"
                  )}
                </Button>
              </Stack>

              <AccountInput
                name="mbr_pw"
                label="비밀번호"
                type="password"
                required
              />
              <AccountInput
                name="mbr_pw_check"
                label="비밀번호 확인"
                type="passwordCheck"
                required
              />
              <AccountInput name="mbr_nm" label="이름" required />
              <AccountInput
                name="mbr_tel"
                label="휴대번호"
                placeholder="예) 01012345678"
                type="phone"
                required
              />
              <AccountInput
                name="mbr_email"
                label="이메일"
                type="email"
                placeholder="예) test@corp.com"
                required
              />
              <AccountInput
                name="mbr_empl_dt"
                label="입사일"
                type="date"
                required
              />
            </Stack>
            <div style={{ marginBottom: "15px" }}>
              <div>
                <Checkbox
                  checked={terms1 && terms2}
                  onChange={() => {
                    if (terms1 && terms2) {
                      setTerms1(false);
                      setTerms2(false);
                    } else {
                      setTerms1(true);
                      setTerms2(true);
                    }
                  }}
                />{" "}
                전체동의
              </div>
              <div>
                <Checkbox
                  checked={terms1}
                  onChange={() => setTerms1(curr => !curr)}
                />{" "}
                이용약관 동의(필수){" "}
                <Button
                  variant="outlined"
                  size="small"
                  color="inherit"
                  onClick={() => setTerms1Modal(true)}
                >
                  내용확인
                </Button>
              </div>
              <div>
                <Checkbox
                  checked={terms2}
                  onChange={() => setTerms2(curr => !curr)}
                />{" "}
                개인정보 수집 및 이용 동의(필수){" "}
                <Button
                  variant="outlined"
                  size="small"
                  color="inherit"
                  onClick={() => setTerms2Modal(true)}
                >
                  내용확인
                </Button>
              </div>
            </div>
            <Typography color="primary">
              • 계정신청완료 후 관리자 승인 시 시스템 접속이 가능합니다
            </Typography>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              size="large"
              fullWidth
              disabled={!(terms1 && terms2)}
            >
              계정 신청하기
            </Button>
          </form>
        </FormProvider>
      </Box>
      {subtitle}

      <Modal
        isView={terms1Modal}
        title={"이용약관"}
        cancelAction={() => setTerms1Modal(false)}
      >
        <div>
          이용약관 동의이용약관 동의이용약관 동의이용약관 동의이용약관
          동의이용약관 동의이용약관 동의
        </div>
      </Modal>
      <Modal
        isView={terms2Modal}
        title={"개인정보 수집 및 이용"}
        cancelAction={() => setTerms2Modal(false)}
      >
        <div>
          개인정보 수집 및 이용개인정보 수집 및 이용개인정보 수집 및
          이용개인정보 수집 및 이용개인정보 수집 및 이용개인정보 수집 및
          이용개인정보 수집 및 이용
        </div>
      </Modal>
    </>
  );
};

const SignUp = () => {
  return (
    <PageContainer>
      <Box
        sx={{
          position: "relative",
          "&:before": {
            content: '""',
            background: "radial-gradient(#d2f1df, #d3d7fa, #bad8f4)",
            backgroundSize: "400% 400%",
            animation: "gradient 15s ease infinite",
            position: "absolute",
            height: "100%",
            width: "100%",
            opacity: "0.3"
          }
        }}
      >
        <Grid
          container
          spacing={0}
          justifyContent="center"
          sx={{ height: "100vh", overflow: "auto" }}
        >
          <Grid
            item
            xs={12}
            sm={12}
            lg={5}
            xl={4}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Card
              elevation={9}
              sx={{ p: 4, m: 3, zIndex: 1, width: "100%", maxWidth: "450px" }}
            >
              <AuthRegister
                title={
                  <img
                    src={logo}
                    width="200px"
                    style={{ margin: "auto" }}
                    alt=""
                  />
                }
                subtitle={
                  <Stack direction="row" spacing={1} mt={3}>
                    <Typography
                      color="textSecondary"
                      variant="h6"
                      fontWeight="400"
                    >
                      이미 계정이 있으신가요?
                    </Typography>
                    <Typography
                      component={Link}
                      to="/signin"
                      fontWeight="500"
                      sx={{
                        textDecoration: "none",
                        color: "primary.main"
                      }}
                    >
                      로그인
                    </Typography>
                  </Stack>
                }
              />
            </Card>
          </Grid>
        </Grid>
      </Box>
    </PageContainer>
  );
};

export default SignUp;
